import React from 'react';
import ReactDOM from 'react-dom';
import { useData, useField, reportWallClock, fromWallClock } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';

// ── Incident › Times, modeled on ESO ──────────────────────────────────────
// The section shows a read-only summary (the call's times, the standard
// intervals, total call time) with a "Set Times" button; all entry happens
// in a docked Times panel with one row per time and a keypad, as in ESO.

// The main list, in call order. ESO's labels; the NEMSIS element behind each
// is in the title tooltip. Destination and Transfer of Care only appear when
// this unit transports (or when they already hold a value).
const MAIN_TIMES = [
  { elem: 'eTimes.01', label: 'PSAP Call' },
  { elem: 'eTimes.02', label: 'Call Received' },
  { elem: 'eTimes.03', label: 'Dispatched' },
  { elem: 'eTimes.05', label: 'En Route' },
  { elem: 'eTimes.06', label: 'On Scene' },
  { elem: 'eTimes.07', label: 'At Patient' },
  { elem: 'eTimes.09', label: 'Depart Scene' },
  { elem: 'eTimes.11', label: 'At Destination', transport: true },
  { elem: 'eTimes.12', label: 'Transfer of Care', transport: true },
  { elem: 'eTimes.13', label: 'Call Closed' },
];

// Optional/recommended times, kept in a collapsed group in the panel and
// listed in the summary only once they hold a value.
const ADDITIONAL_TIMES = [
  { elem: 'eTimes.04', label: 'Dispatch Acknowledged' },
  { elem: 'eTimes.17', label: 'Arrived at Staging' },
  { elem: 'eTimes.08', label: 'Transfer of EMS Care' },
  { elem: 'eTimes.10', label: 'At Landing Area' },
  { elem: 'eTimes.14', label: 'Unit Canceled' },
  { elem: 'eTimes.16', label: 'Call Completed' },
  { elem: 'eTimes.15', label: 'Back at Home Location' },
];

const ALL_TIMES = [...MAIN_TIMES, ...ADDITIONAL_TIMES];

// eDisposition.30 values where this unit carries the patient.
const TRANSPORT_CODES = ['4230001', '4230003'];

// Intervals as ESO shows them: [label, start element(s), end element].
// The first start element that has a value is used.
const INTERVALS = [
  { key: 'chute',      label: 'Chute',      from: ['eTimes.03'], to: 'eTimes.05', def: 'Dispatched to En Route' },
  { key: 'scene',      label: 'On Scene',   from: ['eTimes.06'], to: 'eTimes.09', def: 'On Scene to Depart Scene' },
  { key: 'transport',  label: 'Transport',  from: ['eTimes.09'], to: 'eTimes.11', def: 'Depart Scene to At Destination', transport: true },
  { key: 'turnaround', label: 'Turnaround', from: ['eTimes.12', 'eTimes.11'], to: 'eTimes.13', def: 'Transfer of Care (or At Destination) to Call Closed', transport: true },
];
const RESPONSE = { label: 'Response Time', from: ['eTimes.03'], to: 'eTimes.06', def: 'Dispatched to On Scene' };
const TOTAL = { label: 'Total Call Time', from: ['eTimes.01', 'eTimes.02', 'eTimes.03'], to: 'eTimes.13', def: 'PSAP Call (or the earliest call time) to Call Closed' };

const EMPTY = '- -';

function ms(v) {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
}

// Duration as H:MM:SS, or M:SS under an hour.
function formatDuration(msDiff) {
  if (msDiff == null || msDiff < 0) return null;
  const total = Math.round(msDiff / 1000);
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const pad = n => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function intervalText(values, iv, transporting) {
  if (iv.transport && !transporting) return 'N/A';
  const start = iv.from.map(e => ms(values[e])).find(t => t != null);
  const end = ms(values[iv.to]);
  if (start == null || end == null) return EMPTY;
  return formatDuration(end - start) ?? EMPTY;
}

function formatDateLong(ymd) {
  if (!ymd) return EMPTY;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayYmd() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function nowHms() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Reads every eTimes element (plus transport disposition) from the report.
function useTimes() {
  const data = useData();
  const values = {};
  for (const t of ALL_TIMES) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    values[t.elem] = useField(data.path(t.elem) || '');
  }
  const disposition = useField(data.path('eDisposition.30') || '');
  const transporting = TRANSPORT_CODES.includes(disposition) || !!values['eTimes.11'] || !!values['eTimes.12'];
  return { values, transporting };
}

function incidentDate(values) {
  const first = ALL_TIMES.map(t => reportWallClock(values[t.elem])).filter(Boolean).sort()[0];
  return first ? first.slice(0, 10) : '';
}

export function TimesSummary() {
  const { values, transporting } = useTimes();
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [showDefs, setShowDefs] = React.useState(false);
  const date = incidentDate(values);

  const listed = [
    ...MAIN_TIMES.filter(t => !t.transport || transporting),
    ...ADDITIONAL_TIMES.filter(t => values[t.elem]),
  ];

  return (
    <div className="tm-summary">
      <div className="tm-card tm-head">
        <div className="tm-date">
          <span className="tm-muted">Incident Date</span>
          <strong>{formatDateLong(date)}</strong>
        </div>
        <button className="tm-set" onClick={() => setPanelOpen(true)}>Set Times</button>
      </div>
      <div className="tm-grid">
        <div className="tm-card tm-list">
          {listed.map(t => (
            <div className="tm-row" key={t.elem} title={t.elem} id={`field-${t.elem}`}>
              <span>{t.label}</span>
              <span className="tm-val">{reportWallClock(values[t.elem]).slice(11, 19) || EMPTY}</span>
            </div>
          ))}
        </div>
        <div className="tm-side">
          <div className="tm-card tm-intervals">
            <div className="tm-big">
              <span className="tm-muted">{RESPONSE.label}</span>
              <strong>{intervalText(values, RESPONSE, transporting)}</strong>
            </div>
            {INTERVALS.map(iv => (
              <div className="tm-row" key={iv.key}>
                <span>{iv.label}</span>
                <span className="tm-val">{intervalText(values, iv, transporting)}</span>
              </div>
            ))}
            <button className="tm-link" onClick={() => setShowDefs(s => !s)}>
              {showDefs ? 'Hide Definitions' : 'View Definitions'}
            </button>
            {showDefs && (
              <dl className="tm-defs">
                {[RESPONSE, ...INTERVALS, TOTAL].map(iv => (
                  <React.Fragment key={iv.label}><dt>{iv.label}</dt><dd>{iv.def}</dd></React.Fragment>
                ))}
              </dl>
            )}
          </div>
          <div className="tm-card tm-big tm-total">
            <span className="tm-muted">{TOTAL.label}</span>
            <strong>{intervalText(values, TOTAL, transporting)}</strong>
          </div>
        </div>
      </div>
      {panelOpen && <TimesPanel onClose={() => setPanelOpen(false)} />}
    </div>
  );
}

const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '', '0', 'C'];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

function TimesPanel({ onClose }) {
  const data = useData();
  const { values, transporting } = useTimes();
  const defaultDate = incidentDate(values) || todayYmd();

  const [draft, setDraft] = React.useState(() => {
    const d = {};
    for (const t of ALL_TIMES) {
      const wall = reportWallClock(values[t.elem]);
      d[t.elem] = { time: wall.slice(11, 19), date: wall.slice(0, 10) || defaultDate };
    }
    return d;
  });
  const [showMore, setShowMore] = React.useState(() => ADDITIONAL_TIMES.some(t => values[t.elem]));
  const rows = [
    ...MAIN_TIMES.filter(t => !t.transport || transporting),
    ...(showMore ? ADDITIONAL_TIMES : []),
  ];
  const [active, setActive] = React.useState(rows[0].elem);
  const activeIdx = rows.findIndex(r => r.elem === active);
  const activeRow = rows[activeIdx] || rows[0];

  const setRow = (elem, patch) => setDraft(d => ({ ...d, [elem]: { ...d[elem], ...patch } }));
  const move = (dir) => {
    const next = rows[activeIdx + dir];
    if (next) setActive(next.elem);
  };

  // Keypad digits fill HH:MM:SS with the colons added as you type; the
  // sixth digit moves on to the next time, as ESO's entry does.
  const handleKey = (k) => {
    if (!k) return;
    if (k === 'C') { setRow(active, { time: '' }); return; }
    const digits = (draft[active].time.replace(/\D/g, '').length >= 6 ? '' : draft[active].time.replace(/\D/g, '')) + k;
    setRow(active, { time: digits.replace(/(\d{2})(?=\d)/g, '$1:') });
    if (digits.length === 6) move(1);
  };

  const invalid = rows.find(r => draft[r.elem].time && (!TIME_RE.test(draft[r.elem].time) || !draft[r.elem].date));

  const handleOk = () => {
    if (invalid) { setActive(invalid.elem); return; }
    for (const t of ALL_TIMES) {
      const { time, date } = draft[t.elem];
      const prev = values[t.elem] || '';
      const next = time ? fromWallClock(`${date}T${time.length === 5 ? time + ':00' : time}`, prev) : '';
      if (next !== prev && (next || prev)) data.setReportPath(data.path(t.elem), next);
    }
    onClose();
  };

  const row = (t) => {
    const d = draft[t.elem];
    const bad = d.time && !TIME_RE.test(d.time);
    return (
      <div key={t.elem} className={"tm-prow" + (active === t.elem ? " active" : "")} onClick={() => setActive(t.elem)} title={t.elem}>
        <span className="tm-plabel">{t.label}</span>
        <input className={"tm-ptime" + (bad ? " invalid" : "")} type="text" inputMode="numeric" placeholder="hh:mm:ss"
          value={d.time} onFocus={() => setActive(t.elem)}
          onChange={e => setRow(t.elem, { time: e.target.value })} />
        <input className="tm-pdate" type="date" value={d.date}
          onFocus={() => setActive(t.elem)}
          onChange={e => setRow(t.elem, { date: e.target.value })} />
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="epcr-pm-back" onClick={onClose}>
      <div className="epcr-tm epcr-root" onClick={e => e.stopPropagation()}>
        <div className="tm-phead">
          <h3>Times</h3>
          <button className="tm-ok" onClick={handleOk}>OK</button>
          <button className="tm-close" onClick={onClose} aria-label="Close"><Icons.close size={22} /></button>
        </div>
        <div className="tm-pbody">
          <div className="tm-prows">
            {MAIN_TIMES.filter(t => !t.transport || transporting).map(row)}
            <button className="tm-more" onClick={() => setShowMore(s => !s)}>
              {showMore ? <Icons.chev size={18} /> : <Icons.chevRight size={18} />} Additional times
            </button>
            {showMore && ADDITIONAL_TIMES.map(row)}
          </div>
          <div className="tm-pside">
            <div className="tm-pside-head">
              <span>{activeRow.label}</span>
              <button className="tm-arrow" onClick={() => move(-1)} disabled={activeIdx <= 0} aria-label="Previous time"><Icons.chevLeft size={22} /></button>
              <button className="tm-arrow" onClick={() => move(1)} disabled={activeIdx >= rows.length - 1} aria-label="Next time"><Icons.chevRight size={22} /></button>
            </div>
            <div className="tm-keypad">
              {KEYS.map((k, i) => k
                ? <button key={i} className={"tm-key" + (k === 'C' ? " clear" : "")} onMouseDown={e => e.preventDefault()} onClick={() => handleKey(k)}>{k}</button>
                : <span key={i} />)}
            </div>
            <button className="tm-link" onClick={() => setRow(active, { time: nowHms(), date: todayYmd() })}>
              Set to current date &amp; time
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
